import {
	ContainerFilled,
	ExpandOutlined,
	FileUnknownFilled,
	MailFilled,
	MessageFilled,
	PhoneFilled,
	PushpinOutlined,
	SoundFilled,
	WarningFilled,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Image } from "components/Image/Image"
import { Input } from "components/Input/Input"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useDisclosure } from "hooks/useDisclosure"
import { ReplyIcon } from "icons/ReplyIcon"
import {
	CallStatus,
	ChatMessage,
	MessageStatus,
	MessageType,
	MessageTypeLabel,
} from "interface/communications"
import { useFlag } from "modules/flags/flags"
import { ComponentProps, memo, useCallback, useMemo, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useRetryMessage, useSendMessage } from "services/communications"
import { usePinMessage } from "services/patients"
import { cn } from "styles/cn"
import { asDate } from "utils/date"
import { dataUrlToFile, downloadFileFromUrl } from "utils/file"
import { AudioMessage } from "./AudioMessage"
import { useChat } from "./ChatProvider"

const { ERROR, FAILED, CANCELED, UNDELIVERED } = MessageStatus
const {
	email: EMAIL,
	inApp: INAPP,
	message: MESSAGE,
	voice: VOICE,
	voicemail: VOICEMAIL,
} = MessageType
const SERVER_FAILED_STATUSES = [FAILED, CANCELED, UNDELIVERED]

interface ChatItemsProps {
	messages: ChatMessage[]
	senderId: string
	senderName: string
	alignRight: boolean
}

export function ChatItems({
	messages,
	senderId,
	senderName,
	alignRight,
}: ChatItemsProps) {
	const { notify } = useNotification()
	const {
		patient,
		sender,
		activeParticipant,
		threadId,
		replyTo,
		toggleThread,
	} = useChat()
	const resend = useSendMessage({
		sender,
		patientId: patient.id,
		receiver: activeParticipant.id,
		toType: activeParticipant.id === patient.id ? "User" : "AlternateContact",
	})
	const retry = useRetryMessage(patient.id)
	const highlight = sender.id === senderId
	const sentByPatient = senderId === patient.id

	return (
		<ul
			aria-labelledby={`list-${senderId}`}
			className="flex flex-col gap-1 py-1"
		>
			<p
				id={`list-${senderId}`}
				className={cn(
					"pr-10 pl-10 text-sm text-navy-60",
					alignRight && "pr-10 text-right",
					highlight && "font-semibold",
					sentByPatient && "hidden",
				)}
			>
				{senderName}
			</p>
			{messages.map((message, index) => {
				const messageTimestamp = asDate(message.timestamp).format("HH:mm:ss A")
				const messageAriaLabel = `${MessageTypeLabel[message.type]} at ${messageTimestamp} ${!message.isRead ? "(unread)" : ""}`
				const viewEmailThreadFromRight =
					alignRight &&
					message.type === EMAIL &&
					!!message.threadId &&
					!threadId
				const showAvatar =
					index === messages.length - 1 || messages.length === 1
				const hasSendError =
					alignRight &&
					(!!message.text || !!message.mediaUrl) &&
					[...SERVER_FAILED_STATUSES, ERROR].includes(
						message.status as MessageStatus,
					)
				const isPending =
					(resend.isPending &&
						resend.variables.text === message.text &&
						resend.variables.id === message.id) ||
					(retry.isPending && retry.variables === message.id)
				const handleResend = async () => {
					if (
						message.status === ERROR &&
						(!!message.text || !!message.mediaUrl)
					) {
						resend.mutate(
							{
								type: message.type,
								text:
									(message.text ?? "").length > 0 ? message.text : undefined,
								id: message.id,
								file: message.mediaUrl
									? await dataUrlToFile(
											message.mediaUrl!,
											message.mediaOriginalName,
										)
									: undefined,
								subject:
									(message.subject ?? "").length > 0
										? message.subject
										: undefined,
								inReplyTo: message.inReplyTo,
							},
							{
								onError: (error) => {
									notify.error({
										message: error.message,
									})
								},
							},
						)
					} else if (
						SERVER_FAILED_STATUSES.includes(message.status as MessageStatus)
					) {
						retry.mutate(message.id, {
							onError: (error) => {
								notify.error({
									message: error.message,
								})
							},
						})
					}
				}

				return (
					<li
						key={`chat-items-${message.id}`}
						aria-label={messageAriaLabel}
						tabIndex={0}
						className="focus-outline group rounded-xl outline-offset-1!"
					>
						<div
							className={cn(
								"flex w-full items-end justify-start gap-2",
								alignRight && "justify-end",
							)}
						>
							{hasSendError && (
								<WarningFilled
									aria-hidden
									className="p-1 text-yellow-orange-60"
								/>
							)}
							<AvatarWithBadge
								className={cn(alignRight && "order-2", showAvatar && "visible")}
								alignRight={alignRight}
								type={message.type}
							>
								{senderName}
							</AvatarWithBadge>
							<div
								className={cn(
									"order-2 max-w-[70%] overflow-hidden rounded-xl rounded-bl-xs bg-grey-20 text-navy-100",
									alignRight &&
										"order-1 rounded-br-xs rounded-bl-xl bg-mint-100 text-white",
								)}
							>
								<MessageContent message={message} alignRight={alignRight} />
							</div>
							{!alignRight && (
								<div className="order-3 flex gap-1 self-start">
									{!sender.isPatient && !message.isRead && (
										<div className="-ml-3 size-1.5 rounded-full bg-blue-60" />
									)}
									<div className="hidden flex-col gap-2 group-focus-within:flex group-hover:flex">
										{message.type === EMAIL && !threadId && (
											<>
												{!sender.isPatient && (
													<Button
														size="small"
														icon={<ReplyIcon aria-hidden />}
														aria-label="Reply to email"
														onClick={() => replyTo(message)}
													/>
												)}
												{!!message.threadId && (
													<Button
														size="small"
														icon={<ExpandOutlined aria-hidden />}
														aria-label="View email thread"
														onClick={() => toggleThread(message.threadId)}
													/>
												)}
											</>
										)}
										{message.text && !sender.isPatient && (
											<PinMessageToProfile
												patientId={patient.id}
												message={message}
											/>
										)}
									</div>
								</div>
							)}

							{viewEmailThreadFromRight && (
								<Button
									size="small"
									className="order-0 hidden self-start group-focus-within:flex group-hover:flex"
									icon={<ExpandOutlined aria-hidden />}
									aria-label="View email thread"
									onClick={() => toggleThread(message.threadId)}
								/>
							)}
						</div>

						{hasSendError && (
							<div className="flex items-center justify-end gap-1 pr-10 text-sm">
								This message wasn't delivered.
								<Button
									type="link"
									size="small"
									loading={isPending}
									className="px-0 text-sm text-yellow-orange-60 underline"
									onClick={handleResend}
								>
									Click to send again
								</Button>
							</div>
						)}
					</li>
				)
			})}
		</ul>
	)
}

const AvatarWithBadge = memo(function MessageBadge({
	className,
	alignRight,
	type,
	children,
	...props
}: ComponentProps<"div"> & {
	alignRight: boolean
	type: MessageType
}) {
	const MessageBadge = useMemo(() => {
		if (type === MESSAGE) {
			return MessageFilled
		} else if (type === EMAIL) {
			return MailFilled
		} else if (type === INAPP) {
			return ContainerFilled
		} else if (type === VOICE) {
			return PhoneFilled
		} else if (type === VOICEMAIL) {
			return SoundFilled
		}
		return null
	}, [type])

	return (
		<div {...props} className={cn("invisible relative order-1", className)}>
			<Avatar className="size-8 lg:size-8">{children}</Avatar>

			{MessageBadge && (
				<MessageBadge
					aria-hidden
					className={cn(
						"absolute -right-1 -bottom-1 size-4 place-content-center rounded-full bg-white text-xs text-navy-60 shadow-sm",
						alignRight && "-left-1",
					)}
				/>
			)}
		</div>
	)
})

function MessageContent({
	message,
	alignRight,
}: {
	message: ChatMessage
	alignRight: boolean
}) {
	const {
		text,
		mediaUrl = "",
		mediaOriginalName = `${message.timestamp}.png`,
		type,
		subject = "",
	} = message
	const hasMedia = !!message.mediaUrl
	const isCall = [VOICE, VOICEMAIL].includes(type)
	const isText = [MESSAGE, EMAIL, INAPP].includes(type)
	const theme = alignRight ? "light" : "dark"
	const [mediaLoadSuccess, setMediaLoadSuccess] = useState(hasMedia)
	const fileName = useMemo(() => {
		if (mediaOriginalName.length === 0 && isText) {
			const url = new URL(mediaUrl)
			return url.pathname.split("/").pop() ?? ""
		}

		return mediaOriginalName
	}, [isText, mediaOriginalName, mediaUrl])

	return (
		<div
			className={cn(
				"flex flex-col gap-1 px-3 py-2 whitespace-pre-wrap",
				!mediaLoadSuccess && "min-w-0",
			)}
		>
			{isCall && hasMedia && (
				<AudioMessage src={message.mediaUrl!} theme={theme} />
			)}

			{isCall && !hasMedia && (
				<div className="flex items-center justify-center gap-2">
					<PhoneFilled aria-hidden />
					<CallStatusMessage message={message} />
				</div>
			)}

			{subject.length > 0 && (
				<>
					<h3>{subject}</h3>
					<Divider
						className={cn(
							"my-0 border-grey-40",
							theme === "light" && "border-white",
						)}
					/>
				</>
			)}

			{isText && text}

			{isText && !text && !hasMedia && "(No text)"}

			{isText && hasMedia && !mediaLoadSuccess && (
				<>
					<div className="flex gap-2">
						<FileUnknownFilled
							aria-hidden
							className={cn("text-green-60", theme === "light" && "text-white")}
						/>
						<Tooltip title={`Download file ${fileName}`}>
							<p className="text-wrap">{fileName}</p>
						</Tooltip>
					</div>
					<Button
						type={theme === "dark" ? "primary" : undefined}
						className="w-fit"
						onClick={async () => {
							await downloadFileFromUrl(mediaUrl, fileName)
						}}
					>
						Download
					</Button>
				</>
			)}
			{isText && hasMedia && mediaLoadSuccess && (
				<Image
					alt={fileName}
					src={mediaUrl}
					className="max-h-44"
					preview
					loading="lazy"
					onError={() => {
						setMediaLoadSuccess(false)
					}}
				/>
			)}
		</div>
	)
}

export const CallStatusMessage = ({ message }: { message: ChatMessage }) => {
	const { duration = 0, status, type } = message
	const callStatus = (() => {
		if (status === CallStatus.CANCELED) {
			return "Canceled"
		} else if (status === CallStatus.NO_ANSWER) {
			return "No answer"
		} else if (
			(!status ||
				status === CallStatus.RINGING ||
				status === CallStatus.IN_PROGRESS) &&
			duration === 0 &&
			type === VOICE
		) {
			return "In progress..."
		}
		return status
	})()

	const formatDuration = (duration: number) => {
		if (duration <= 0) {
			return null
		}

		const minutes = Math.floor(duration / 60)
		const seconds = duration % 60
		return minutes > 0
			? `${minutes} minutes ${seconds} seconds`
			: `${seconds} seconds`
	}

	return (
		<span className="flex min-w-0 items-center gap-2">
			<span className="truncate">Voice call</span>
			{duration > 0 || callStatus ? (
				<span className="truncate">
					(
					{formatDuration(duration) ?? (
						<span className="capitalize">{callStatus}</span>
					)}
					)
				</span>
			) : null}
		</span>
	)
}

function PinMessageToProfile({
	message,
	patientId,
}: {
	message: ChatMessage
	patientId: string
}) {
	const { clinicalNotes } = useFlag("clinicalNotes")
	const disclose = useDisclosure<string>()
	const pin = usePinMessage(patientId)
	const { notify } = useNotification()
	const handleOk = useCallback(() => {
		if (!disclose.value) {
			return
		}

		pin.mutate(
			{
				chatMessageId: message.id,
				content: disclose.value,
				chatMessageTimestamp: message.timestamp,
			},
			{
				onSuccess() {
					notify.success({
						message: "Patient profile note updated successfully",
					})
					disclose.close()
				},
			},
		)
	}, [disclose, message.id, message.timestamp, notify, pin])

	useHotkeys("meta+enter", handleOk, {
		enableOnFormTags: ["TEXTAREA"],
	})

	if (!clinicalNotes.enabled) {
		return null
	}

	return (
		<>
			<Tooltip.Dark title="Pin to patient profile">
				<Button
					size="small"
					icon={<PushpinOutlined aria-hidden />}
					aria-label="Pin to patient profile"
					onClick={() => disclose.open(message.text)}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<Modal
					open
					confirmLoading={pin.isPending}
					title="Pin message to patient profile"
					okText="Pin to patient profile"
					cancelText="Cancel"
					onOk={handleOk}
					onCancel={() => {
						pin.reset()
						disclose.close()
					}}
					okButtonProps={{ disabled: !disclose.value?.trim() }}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					<Input.TextArea
						value={disclose.value}
						autoSize={{ minRows: 2 }}
						onChange={(e) => disclose.open(e.target.value)}
					/>
					<ErrorStatus show={pin.isError}>
						An unexpected error occurred. Please, try again.
					</ErrorStatus>
				</Modal>
			)}
		</>
	)
}
